/**
 * Custom Recharge subscription widget.
 *
 * - Row 0: Purchase-type toggle (one-time vs subscription)  [optional]
 * - Row 1: Quantity radio cards (1 / 2 / 3)                 [sub mode only]
 * - Row 2: Frequency dropdown                                [sub mode only]
 * - Live savings label
 *
 * Reads Shopify selling plans (Recharge Quantity Upsell plans) exposed via a
 * JSON script tag inside the element. Selling plan `name` values follow the
 * pattern:
 *   "{qty} product{s} every {frequency} {unit} at {discount}% discount"
 *
 * Writes to hidden `quantity` and `selling_plan` inputs targeting the existing
 * Dawn product form via the `form=""` attribute.
 */
class CustomSubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this._unsubscribeVariantChange = null;
    this._plans = [];
    this._variantPrices = {};
    this._currentVariantId = null;
    this._selectedQuantity = 1;
    this._selectedFrequencyKey = null;
    this._selectedPurchaseType = 'subscription'; // 'subscription' | 'one_time'
    this._frequencies = [];
    // PDP headline price state (see _updatePdpPrice).
    this._pdpPriceEl = null;
    this._pdpPriceOriginalHTML = null;
    this._pdpPriceOriginalClass = null;
    this._pdpRegularLabel = '';
    this._pdpSaleLabel = '';
  }

  connectedCallback() {
    this._sectionId = this.dataset.sectionId;
    this._productFormId = this.dataset.productFormId;
    this._context = this.dataset.context || 'pdp';
    this._currentVariantId = parseInt(this.dataset.currentVariantId, 10) || null;
    this._defaultQuantity = parseInt(this.dataset.defaultQuantity, 10) || 1;
    this._maxQuantity = parseInt(this.dataset.maxQuantity, 10) || 3;
    this._defaultPurchaseType = this.dataset.defaultPurchaseType === 'one_time' ? 'one_time' : 'subscription';
    this._showOneTime = this.dataset.showOneTime === 'true';
    // Free gift threshold (in cents; 0 when the milestone is off in theme settings).
    this._freeGiftThreshold = parseInt(this.dataset.freeGiftThreshold, 10) || 0;
    this._freeGiftActive = this.dataset.freeGiftActive === 'true';
    // 'subscription' = any subscription earns the gift; 'spend' = threshold.
    this._giftTrigger = this.dataset.freeGiftTrigger === 'spend' ? 'spend' : 'subscription';
    // Free delivery spend threshold (in cents; 0 when the progress bar is off).
    // 'cards' = the quantity radio group (stacked rows on the PDP, a 3-across
    // grid on product cards), 'stepper' = the -/+ control in both modes,
    // 'auto' = decide from the plans once they are parsed (_resolveQuantityStyle).
    this._quantityStylePreference = ['cards', 'stepper'].includes(this.dataset.quantityStyle)
      ? this.dataset.quantityStyle
      : 'auto';
    this._quantityStyle = this._quantityStylePreference === 'auto' ? 'cards' : this._quantityStylePreference;
    // Kaching Bundles owns quantity while one-time is selected. Mounted lazily —
    // see _mountKaching() — so a session that never leaves subscribe never loads
    // the app at all.
    this._hasKaching = this.dataset.kachingBundles === 'true';
    this._kachingMounted = false;

    // PDP deep-link pre-selection (set by the card Subscribe CTA):
    //   ?csw_subscribe=1&csw_quantity=N
    if (this._context !== 'card') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('csw_subscribe') === '1') {
        this._defaultPurchaseType = 'subscription';
      }
      const qp = parseInt(params.get('csw_quantity'), 10);
      if (!isNaN(qp) && qp >= 1) {
        // Kept apart from _defaultQuantity, which stays the configured default
        // so that switching purchase type still returns to the merchandised
        // option rather than to whatever a link happened to carry.
        this._deepLinkQuantity = Math.min(qp, this._maxQuantity);
      }
    }

    this._quantityInput = this.querySelector('[data-role="quantity"]');
    this._sellingPlanInput = this.querySelector('[data-role="selling-plan"]');
    this._savingsEl = this.querySelector('[data-role="savings"]');
    this._frequencySelect = this.querySelector('[data-role="frequency-select"]');
    this._qtyCards = Array.from(this.querySelectorAll('[data-role="qty-card"]'));
    this._ptCards = Array.from(this.querySelectorAll('[data-role="pt-card"]'));
    this._subOptions = this.querySelector('[data-role="sub-options"]');
    this._ptSubscribeSavings = this.querySelector('[data-role="pt-subscribe-savings"]');
    // Card-context CTAs
    this._ctaOneTime = this.querySelector('[data-role="cta-one-time"]');
    this._ctaSubscribe = this.querySelector('[data-role="cta-subscribe"]');
    this._ctaOneTimeLabel = this.querySelector('[data-role="cta-one-time-label"]');
    this._ctaOneTimePrice = this.querySelector('[data-role="cta-one-time-price"]');
    this._ctaSubscribeLabel = this.querySelector('[data-role="cta-subscribe-label"]');
    this._ctaSubscribePrice = this.querySelector('[data-role="cta-subscribe-price"]');

    // Stepper (PDP context only)
    this._stepper = this.querySelector('[data-role="qty-stepper"]');
    this._stepperInput = this.querySelector('[data-role="stepper-input"]');
    this._stepperMinus = this.querySelector('[data-role="stepper-minus"]');
    this._stepperPlus = this.querySelector('[data-role="stepper-plus"]');
    // Perk pills inside the Subscribe card (stepper mode only — in cards mode
    // the same information lives on each quantity card).
    this._subPerks = this.querySelector('[data-role="sub-perks"]');
    this._subFreeGift = this.querySelector('[data-role="sub-free-gift"]');
    // Empty container the Kaching Bundles placeholder is inserted into.
    this._kachingMount = this.querySelector('[data-role="kaching-mount"]');
    // Purchase-type Subscribe card (so we can hide it when qty > max)
    this._ptSubscribeCard = this.querySelector('[data-role="pt-card"][data-purchase-type="subscription"]');
    this._frequencyWrapper = this.querySelector('.custom-subscription-widget__frequency');

    // PDP submit button (rendered by custom-buy-buttons.liquid outside this element).
    this._pdpSubmitButton = document.getElementById(`ProductSubmitButton-${this._sectionId}`);
    this._pdpCtaTextEl = this._pdpSubmitButton
      ? this._pdpSubmitButton.querySelector('[data-role="pdp-cta-text"]')
      : null;
    // Cache the initial (server-rendered) label so we can restore for sold-out/unknown states.
    this._pdpCtaInitialText = this._pdpCtaTextEl ? this._pdpCtaTextEl.textContent.trim() : '';
    // Reassurance note under the PDP CTA, shown only while subscription is selected.
    this._pdpCtaNote = document.getElementById(`ProductCtaNote-${this._sectionId}`);

    this._parseData();

    if (this._plans.length === 0) {
      // No selling plans: nothing to do besides the plain quantity hidden input.
      return;
    }

    this._buildFrequencies();
    this._resolveQuantityStyle();
    this._selectedQuantity = this._deepLinkQuantity || this._defaultQuantity;
    this._selectedFrequencyKey = this._frequencies[0]?.key || null;
    this._selectedPurchaseType = this._defaultPurchaseType;

    this._renderFrequencyOptions();
    this._bindEvents();
    this._syncSelectedUI();
    this._applyPurchaseTypeVisibility();
    this._updateFormInputs();
    this._updateSavings();
    this._updateQtyCardDiscounts();
    this._updatePurchaseTypeSummaries();
    this._updateCtaLabels();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId && event.data.sectionId !== this._sectionId) return;
        const newId = event?.data?.variant?.id;
        if (newId) {
          this._currentVariantId = newId;
          // Option labels quote a saving against this variant's RRP, so re-render.
          this._renderFrequencyOptions();
          this._updateSavings();
          this._updateQtyCardDiscounts();
          this._updatePurchaseTypeSummaries();
          this._updateCtaLabels();
        }
      });
    }
  }

  disconnectedCallback() {
    if (this._unsubscribeVariantChange) this._unsubscribeVariantChange();
  }

  _parseData() {
    const plansScript = this.querySelector('script[data-selling-plans]');
    const pricesScript = this.querySelector('script[data-variant-prices]');
    try {
      this._plans = plansScript ? JSON.parse(plansScript.textContent) : [];
    } catch (e) {
      console.error('[custom-subscription-widget] Failed to parse selling plans JSON', e);
      this._plans = [];
    }
    try {
      this._variantPrices = pricesScript ? JSON.parse(pricesScript.textContent) : {};
    } catch (e) {
      console.error('[custom-subscription-widget] Failed to parse variant prices JSON', e);
      this._variantPrices = {};
    }
  }

  /**
   * Fallback copy of parsePlanName in assets/subscription-plans.js, which
   * documents the plan shapes. Only reached if that script hasn't loaded.
   */
  _parsePlanName(name) {
    if (!name || typeof name !== 'string') return null;
    const re = /^(?:(\d+)\s+products?\s+)?every\s+(?:(\d+)\s+)?(day|days|week|weeks|month|months|year|years)(?:\s+at\s+(\d+(?:\.\d+)?)%\s+discount)?/i;
    const m = name.trim().match(re);
    if (!m) return null;
    const qty = m[1] ? parseInt(m[1], 10) : null;
    const freq = m[2] ? parseInt(m[2], 10) : 1;
    let unit = m[3].toLowerCase();
    if (unit.endsWith('s')) unit = unit.slice(0, -1);
    const discount = m[4] ? parseFloat(m[4]) : null;
    return {
      quantity: qty,
      intervalFrequency: freq,
      intervalUnit: unit,
      discountPercent: discount,
    };
  }

  /**
   * Fallback copy of parsePlan in assets/subscription-plans.js: Recharge's
   * plan options give the frequency and quantity, the name fills any gaps.
   */
  _parsePlan(plan) {
    const parsed = this._parsePlanName(plan.name) || {
      quantity: null,
      intervalFrequency: null,
      intervalUnit: null,
      discountPercent: null,
    };
    const option = (name) => {
      const opt = (plan.options || []).find((o) => o && String(o.name).trim().toLowerCase() === name);
      return opt && opt.value != null ? String(opt.value).trim() : '';
    };
    const freq = option('order frequency and unit').match(/^(\d+)[-\s]*(day|week|month|year)s?$/i);
    if (freq) {
      parsed.intervalFrequency = parseInt(freq[1], 10);
      parsed.intervalUnit = freq[2].toLowerCase();
    }
    const qty = parseInt(option('product quantity'), 10);
    if (qty > 0) parsed.quantity = qty;
    return parsed.intervalUnit ? parsed : null;
  }

  /**
   * Prefer the price adjustment over the plan name — the adjustment is what
   * Shopify charges, while the name is merchant copy that may quote a headline
   * figure bundling other discounts. See assets/subscription-plans.js.
   */
  _extractDiscountFromPlan(plan, parsed) {
    const adj = plan.price_adjustments && plan.price_adjustments[0];
    if (adj && adj.value_type === 'percentage') {
      const value = parseFloat(adj.value);
      if (!isNaN(value)) return value;
    }
    if (parsed && typeof parsed.discountPercent === 'number' && !isNaN(parsed.discountPercent)) {
      return parsed.discountPercent;
    }
    return 0;
  }

  _buildFrequencies() {
    // Prefer the shared helper (assets/subscription-plans.js); fall back to the
    // local implementation if it hasn't loaded for any reason.
    if (window.SubscriptionPlans && typeof window.SubscriptionPlans.buildFrequencies === 'function') {
      this._frequencies = window.SubscriptionPlans.buildFrequencies(this._plans);
      return;
    }
    const buckets = new Map();
    this._plans.forEach((plan, index) => {
      const parsed = this._parsePlan(plan);
      if (!parsed) return;
      const key = `${parsed.intervalUnit}:${parsed.intervalFrequency}`;
      if (!buckets.has(key)) {
        buckets.set(key, {
          key,
          intervalUnit: parsed.intervalUnit,
          intervalFrequency: parsed.intervalFrequency,
          label: this._frequencyLabel(parsed.intervalUnit, parsed.intervalFrequency),
          sortKey: this._frequencySortKey(parsed.intervalUnit, parsed.intervalFrequency),
          sourceIndex: index,
          plansByQty: {},
          anyQtyPlan: null,
        });
      }
      const bucket = buckets.get(key);
      const entry = {
        id: plan.id,
        discountPercent: this._extractDiscountFromPlan(plan, parsed),
        raw: plan,
      };
      // A flat plan names no quantity, so it covers all of them.
      if (parsed.quantity === null) {
        bucket.anyQtyPlan = entry;
      } else {
        bucket.plansByQty[parsed.quantity] = entry;
      }
    });
    // Order follows Recharge's plan order (Map preserves insertion order).
    this._frequencies = Array.from(buckets.values());
  }

  /**
   * The plan covering `qty` in a frequency bucket, or null. A quantity-specific
   * plan wins over a flat wildcard, so the two can coexist.
   */
  _planForQuantity(freq, qty) {
    if (!freq) return null;
    if (window.SubscriptionPlans && typeof window.SubscriptionPlans.planForQuantity === 'function') {
      return window.SubscriptionPlans.planForQuantity(freq, qty);
    }
    return (freq.plansByQty && freq.plansByQty[qty]) || freq.anyQtyPlan || null;
  }

  /**
   * Highest quantity tier on offer, preferring `freq`'s own tiers so the chosen
   * delivery interval is kept. Null when the plans define no explicit tiers.
   */
  _highestTierPlan(freq) {
    const best = (bucket) => {
      if (!bucket || !bucket.plansByQty) return null;
      const tiers = Object.keys(bucket.plansByQty)
        .map(Number)
        .filter((n) => !isNaN(n))
        .sort((a, b) => b - a);
      return tiers.length ? bucket.plansByQty[tiers[0]] : null;
    };
    const preferred = best(freq);
    if (preferred) return preferred;
    for (const f of this._frequencies) {
      const plan = best(f);
      if (plan) return plan;
    }
    return null;
  }

  /**
   * The plan to actually use for `qty` at `freq`.
   *
   * Exact tier first. Failing that — and only where the stepper is the quantity
   * control — the highest tier available, so a quantity beyond the configured
   * tiers still subscribes rather than silently losing its selling plan. That
   * mirrors what the cart drawer's upgrade control already does for the same
   * situation (see _maxTierPlan in cart-subscription-upgrade.js).
   *
   * With the quantity boxes there is no fallback: the boxes only ever offer
   * quantities that have a tier, so anything past them is genuinely off-plan.
   */
  _resolvePlan(freq, qty) {
    const exact = this._planForQuantity(freq, qty);
    if (exact) return exact;
    if (this._quantityStyle !== 'stepper') return null;
    return this._highestTierPlan(freq);
  }

  /**
   * Can `qty` be subscribed to at all?
   *
   * Asked of the plans rather than the max_quantity setting on purpose. With
   * quantity-upsell plans (1/2/3) and the quantity boxes this answers exactly
   * as `qty > maxQuantity` did. With a flat wildcard plan it is always true, so
   * the subscribe cap disappears on its own when Recharge is reconfigured — no
   * code change, and no stale setting silently capping a product.
   *
   * In stepper mode it is also always true while any plan exists: the quantity
   * control must not be able to decide whether subscribing is on offer.
   */
  _canSubscribeAtQuantity(qty) {
    return this._frequencies.some((f) => !!this._resolvePlan(f, qty));
  }

  /** Largest subscribable quantity at or below `qty`, for snapping back. */
  _nearestSubscribableQuantity(qty) {
    if (this._canSubscribeAtQuantity(qty)) return qty;
    const start = Math.min(qty, this._maxQuantity);
    for (let q = start; q >= 1; q -= 1) {
      if (this._canSubscribeAtQuantity(q)) return q;
    }
    return 1;
  }

  /** Every plan in a bucket, wildcard included. */
  _plansIn(freq) {
    if (!freq) return [];
    const plans = Object.values(freq.plansByQty || {});
    if (freq.anyQtyPlan) plans.push(freq.anyQtyPlan);
    return plans;
  }

  _frequencyLabel(unit, freq) {
    const unitPlural = freq === 1 ? unit : `${unit}s`;
    if (freq === 1) return `Every ${unit}`;
    return `Every ${freq} ${unitPlural}`;
  }

  _frequencySortKey(unit, freq) {
    const days = { day: 1, week: 7, month: 30, year: 365 }[unit] || 30;
    return days * freq;
  }

  _renderFrequencyOptions() {
    if (!this._frequencySelect) return;
    this._frequencySelect.innerHTML = '';
    // One saving across every option tells the shopper nothing about which to
    // pick, so the suffix is dropped and the figure left to the Subscribe card.
    const flat = this._hasFlatDiscount();
    this._frequencies.forEach((f) => {
      const opt = document.createElement('option');
      opt.value = f.key;
      const plan = this._resolvePlan(f, this._selectedQuantity);
      // Quote the saving against RRP, like every other percentage in the widget.
      const savingPct = plan ? this._savingPercentVsRrp(this._selectedQuantity, plan.discountPercent) : 0;
      if (savingPct > 0 && !flat) {
        opt.textContent = `${f.label} (${this._formatPercent(savingPct)} off)`;
      } else {
        opt.textContent = f.label;
      }
      this._frequencySelect.appendChild(opt);
    });
    if (this._selectedFrequencyKey) {
      this._frequencySelect.value = this._selectedFrequencyKey;
    }
  }

  _bindEvents() {
    this._qtyCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        const qty = parseInt(card.dataset.quantity, 10);
        if (!qty) return;
        this._setQuantity(qty, { source: 'card' });
      });
    });

    if (this._frequencySelect) {
      this._frequencySelect.addEventListener('change', () => {
        this._selectedFrequencyKey = this._frequencySelect.value;
        this._updateFormInputs();
        this._updateSavings();
        this._updateQtyCardDiscounts();
        this._updatePurchaseTypeSummaries();
        this._updateCtaLabels();
      });
    }

    this._ptCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        const type = card.dataset.purchaseType;
        if (type !== 'one_time' && type !== 'subscription') return;
        // If switching to subscribe while qty is over the max, snap qty back to the max.
        if (type === 'subscription' && !this._canSubscribeAtQuantity(this._selectedQuantity)) {
          this._setQuantity(this._nearestSubscribableQuantity(this._selectedQuantity), {
            source: 'purchase-type',
          });
        }
        this._setPurchaseType(type);
      });
    });

    // Stepper (PDP context only). Increments/decrements one-time quantity beyond
    // the card range. Snaps back to a card when qty <= maxQuantity.
    if (this._stepperMinus) {
      this._stepperMinus.addEventListener('click', (e) => {
        e.preventDefault();
        const next = Math.max(1, this._selectedQuantity - 1);
        this._setQuantity(next, { source: 'stepper' });
      });
    }
    if (this._stepperPlus) {
      this._stepperPlus.addEventListener('click', (e) => {
        e.preventDefault();
        const next = this._selectedQuantity + 1;
        this._setQuantity(next, { source: 'stepper' });
      });
    }
    if (this._stepperInput) {
      this._stepperInput.addEventListener('change', () => {
        const raw = parseInt(this._stepperInput.value, 10);
        const next = isNaN(raw) || raw < 1 ? 1 : raw;
        this._setQuantity(next, { source: 'stepper' });
      });
    }

    if (this._ctaOneTime) {
      this._ctaOneTime.addEventListener('click', (e) => {
        e.preventDefault();
        this._submitAs('one_time');
      });
    }
    if (this._ctaSubscribe) {
      this._ctaSubscribe.addEventListener('click', (e) => {
        e.preventDefault();
        // On product cards, Subscribe deep-links to the PDP with the chosen
        // quantity + subscription pre-selected (frequency picked on the PDP).
        if (this._context === 'card') {
          this._navigateToProductForSubscription();
        } else {
          this._submitAs('subscription');
        }
      });
    }
  }

  /**
   * Build a PDP URL that pre-selects a subscription + the chosen quantity via
   * namespaced query params, then navigate to it. Used by the card Subscribe CTA.
   */
  _navigateToProductForSubscription() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) {
      // Fallback: no URL available, add to cart directly.
      this._submitAs('subscription');
      return;
    }
    const url = new URL(productUrl, window.location.origin);
    if (this._currentVariantId) url.searchParams.set('variant', String(this._currentVariantId));
    url.searchParams.set('csw_subscribe', '1');
    url.searchParams.set('csw_quantity', String(this._selectedQuantity || 1));

    if (this._ctaSubscribe) {
      this._ctaSubscribe.classList.add('loading');
      this._ctaSubscribe.setAttribute('aria-disabled', 'true');
      const spinner = this._ctaSubscribe.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }
    window.location.assign(url.toString());
  }

  /**
   * Submit the parent product form with the appropriate selling_plan value.
   * Used by the card-context two-CTA buttons.
   */
  _submitAs(purchaseType) {
    if (!this._sellingPlanInput || !this._quantityInput) return;
    // Sync qty
    this._quantityInput.value = String(this._selectedQuantity);
    // Set/clear selling_plan
    if (purchaseType === 'subscription') {
      const plan = this._currentPlan();
      this._sellingPlanInput.value = plan ? String(plan.id) : '';
    } else {
      this._sellingPlanInput.value = '';
    }
    // Find the parent product form and dispatch its submit event so
    // Dawn's <product-form> handler intercepts and does the AJAX add-to-cart.
    const form = document.getElementById(this._productFormId);
    if (!form) {
      console.warn('[custom-subscription-widget] Product form not found', this._productFormId);
      return;
    }
    // Toggle a loading state on the clicked CTA.
    const cta = purchaseType === 'subscription' ? this._ctaSubscribe : this._ctaOneTime;
    if (cta) {
      cta.classList.add('loading');
      cta.setAttribute('aria-disabled', 'true');
      const spinner = cta.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }
    // Restore CTA state after the cart update publishes.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const restore = () => {
        if (cta) {
          cta.classList.remove('loading');
          cta.removeAttribute('aria-disabled');
          const spinner = cta.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
        unsubCartUpdate();
        unsubCartError();
      };
      const unsubCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, restore);
      const unsubCartError = subscribe(PUB_SUB_EVENTS.cartError, restore);
      // Safety timeout in case neither event fires.
      setTimeout(restore, 10000);
    }
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  /**
   * Central quantity setter — handles card taps, stepper clicks and direct edits.
   *
   * With the quantity boxes, a quantity past the last box has no plan behind it,
   * so we auto-switch to one-time and hide the Subscribe option; coming back
   * under the top box allows subscribing again (but does not auto-flip back).
   *
   * With the stepper this never happens. There the stepper is the only quantity
   * control on the card, so changing it must not change what is being bought —
   * _resolvePlan falls back to the top tier instead.
   */
  _setQuantity(qty, { source } = {}) {
    const next = Math.max(1, parseInt(qty, 10) || 1);
    this._selectedQuantity = next;
    const overMax = !this._canSubscribeAtQuantity(next);

    // Sync radio checked state on qty cards.
    this._qtyCards.forEach((card) => {
      const cardQty = parseInt(card.dataset.quantity, 10);
      const isSelected = !overMax && cardQty === next;
      const radio = card.querySelector('[data-role="qty-radio"]');
      if (radio) radio.checked = isSelected;
      card.classList.toggle('is-selected', isSelected);
    });

    // Reflect in stepper input.
    if (this._stepperInput && this._stepperInput.value !== String(next)) {
      this._stepperInput.value = String(next);
    }

    // Qty exceeds subscribe max: force one-time (subscription plans don't cover
    // this qty). overMax is never true in stepper mode, so this cannot fire
    // there — changing the quantity must never change the purchase type.
    if (overMax && this._selectedPurchaseType !== 'one_time') {
      this._selectedPurchaseType = 'one_time';
      this._ptCards.forEach((card) => {
        const isSelected = card.dataset.purchaseType === 'one_time';
        card.classList.toggle('is-selected', isSelected);
        const radio = card.querySelector('[data-role="pt-radio"]');
        if (radio) radio.checked = isSelected;
      });
    }

    this._applyPurchaseTypeVisibility();
    this._renderFrequencyOptions();
    this._updateFormInputs();
    this._updateSavings();
    this._updateQtyCardDiscounts();
    this._updatePurchaseTypeSummaries();
    this._updateCtaLabels();
  }

  /**
   * Quantity a fresh purchase type starts on: the "Default quantity" setting,
   * the same one the page loads with, so the boxes can be pointed at whichever
   * option is being merchandised — e.g. matching the bar Kaching defaults to.
   *
   * Checked against the boxes actually rendered rather than trusted, since the
   * setting and the number of boxes are configured separately and a default
   * beyond the last box would otherwise select nothing.
   */
  _defaultQuantityOption() {
    const quantities = this._qtyCards
      .map((card) => parseInt(card.dataset.quantity, 10))
      .filter((qty) => !isNaN(qty));
    if (!quantities.length) return this._defaultQuantity;
    return quantities.includes(this._defaultQuantity) ? this._defaultQuantity : quantities[0];
  }

  _setPurchaseType(type) {
    this._selectedPurchaseType = type;
    this._ptCards.forEach((card) => {
      const isSelected = card.dataset.purchaseType === type;
      card.classList.toggle('is-selected', isSelected);
      const radio = card.querySelector('[data-role="pt-radio"]');
      if (radio) radio.checked = isSelected;
    });
    // Switching purchase type returns the quantity to the default, because the
    // two sides are separate controls: subscribe has the boxes, one-time has the
    // stepper or Kaching's bars. Carrying a number across lands it on a control
    // that never offered it, which is where the disagreement between what the
    // page showed and what reached the cart came from. Stepper mode is the
    // exception — there one control serves both, stays on screen throughout, and
    // resetting what the shopper typed would just be rude.
    //
    // _setQuantity then does the whole refresh, which is everything this used to
    // repeat by hand.
    const nextQuantity =
      this._quantityStyle === 'stepper' ? this._selectedQuantity : this._defaultQuantityOption();
    this._setQuantity(nextQuantity);
  }

  _applyPurchaseTypeVisibility() {
    // On product cards there is no purchase-type toggle: sub-options must always
    // remain visible (qty/frequency apply to both CTAs).
    if (this._context === 'card') {
      if (this._subOptions) this._subOptions.hidden = false;
      return;
    }
    const isSub = this._selectedPurchaseType === 'subscription';
    const overMax = !this._canSubscribeAtQuantity(this._selectedQuantity);

    const stepperOnly = this._quantityStyle === 'stepper';
    // Kaching's bars are the quantity control for one-time, so ours stands down.
    const kachingOwnsQuantity = this._hasKaching && !isSub;

    if (kachingOwnsQuantity) this._mountKaching();

    // Qty cards: only meaningful in subscription mode, and not at all when the
    // stepper is the quantity control (they are still server-rendered under
    // 'auto', which only reaches its verdict here).
    const qtyFieldset = this.querySelector('[data-role="quantity-fieldset"]');
    if (qtyFieldset) qtyFieldset.hidden = stepperOnly || !isSub;

    // Stepper. In stepper mode it is the only quantity control there is, so it
    // stays put in both modes and keeps one label. In cards mode it remains the
    // secondary "buy more" control: primary for one-time, otherwise shown only
    // once the customer has gone past what a subscription can cover.
    if (this._stepper) {
      if (kachingOwnsQuantity) {
        this._stepper.hidden = true;
      } else {
        this._stepper.hidden = stepperOnly ? false : isSub && !overMax;
      }
      const stepperLabelEl = this.querySelector('[data-role="stepper-label"]');
      if (stepperLabelEl) {
        if (stepperOnly) {
          stepperLabelEl.textContent = this._stepperPrimaryLabel();
        } else {
          stepperLabelEl.textContent = isSub ? 'Or buy more' : 'Quantity';
        }
      }
    }

    // Frequency dropdown only makes sense for subscriptions with qty <= max.
    if (this._frequencyWrapper) {
      this._frequencyWrapper.hidden = !isSub || overMax;
    }

    // The sub-options wrapper itself stays visible.
    if (this._subOptions) this._subOptions.hidden = false;
    this.classList.toggle('csw--one-time', !isSub);
    this.classList.toggle('csw--subscription', isSub);
    this.classList.toggle('csw--over-max', overMax);
  }

  /**
   * Bring Kaching Bundles up the first time one-time is selected.
   *
   * The snippet sets kachingBundlesDisableAutoInitialize, so the app sits idle
   * until this runs: no block JS, no deal fetch, and — the part that matters —
   * no patched fetch and no add-to-cart click listener. Kaching's interceptor
   * drops our line and substitutes its own items without carrying selling_plan
   * across, so keeping it dormant keeps subscription-only sessions clear of it
   * entirely. Sessions that do land here are covered by the kaching_bundles=true
   * marker in product-form.js.
   *
   * Inserting our own placeholder first also stops Kaching's _addPlaceholders()
   * guessing a position for it just above the add to cart button.
   */
  /**
   * Prices shown on Kaching's currently selected bar, as rendered.
   *
   * Read from the DOM rather than recomputed: bundle discounts are Kaching's to
   * decide, so the only figures guaranteed to agree with what the shopper sees
   * are the ones they are seeing. `now` is '' when the bar or its price is
   * missing, and callers fall back to leaving the price and label alone.
   */
  _kachingSelectedPrice() {
    const empty = { now: '', was: '' };
    if (!this._kachingMount) return empty;
    const bar = this._kachingMount.querySelector('.kaching-bundles__bar--selected');
    if (!bar) return empty;
    const now = bar.querySelector('.kaching-bundles__bar-price');
    const was = bar.querySelector('.kaching-bundles__bar-full-price');
    return {
      now: now ? now.textContent.trim() : '',
      was: was ? was.textContent.trim() : '',
    };
  }

  _mountKaching() {
    if (this._kachingMounted || !this._kachingMount) return;
    this._kachingMounted = true;

    // Follow the bars: headline price and button label both track the selected
    // one, the same way they track a quantity row under Subscribe & Save.
    //
    // Watching the DOM rather than listening for a selection event, because the
    // moments that matter aren't all announced: the bars arriving with one
    // already selected, a per-bar quantity stepper, a variant select inside a
    // bar. Every one of them rewrites the bar's own price text, so that is what
    // we watch. deal-bar-selected is kept alongside as the one explicit signal
    // Kaching does emit, in capture phase — it fires on the block element and
    // may not bubble out to the mount.
    //
    // Reads are coalesced into one animation frame: Kaching re-renders in
    // several passes and this fires on each of them.
    let syncPending = false;
    const syncFromBars = () => {
      if (syncPending) return;
      syncPending = true;
      requestAnimationFrame(() => {
        syncPending = false;
        // Nothing readable means the read landed between Kaching's render
        // passes, with the selected bar momentarily gone — not that the shopper
        // deselected. Acting on it would clear a correct price and label, and no
        // further mutation would arrive to put them back. Clearing belongs to a
        // purchase-type change, which doesn't come through here.
        if (!this._kachingSelectedPrice().now) return;
        this._updatePdpCtaLabel();
        this._updatePdpPrice();
      });
    };
    this._kachingMount.addEventListener('deal-bar-selected', syncFromBars, true);
    // Safe against feedback: everything written back lives outside the mount.
    new MutationObserver(syncFromBars).observe(this._kachingMount, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    const placeholder = document.createElement('kaching-bundle');
    placeholder.setAttribute('product-id', this.dataset.productId);
    this._kachingMount.appendChild(placeholder);

    // Defined by the app embed up front and queued internally if the module has
    // not loaded yet, so this is safe to call at any point.
    if (typeof window.kachingBundlesInitialize === 'function') {
      window.kachingBundlesInitialize();
    }
  }

  /**
   * Does buying more actually get a better rate?
   *
   * Only true when at least two quantities are priced AND their savings differ.
   * Two tiers at the same percentage is not a quantity break — it is one offer
   * described twice, and a grid would invite a choice that changes nothing.
   * A flat wildcard plan prices no explicit quantity at all, so it lands here
   * as false without needing a special case.
   */
  _hasQuantityBreaks() {
    const discountByQty = new Map();
    this._frequencies.forEach((f) => {
      Object.keys(f.plansByQty || {}).forEach((key) => {
        const qty = parseInt(key, 10);
        const pct = f.plansByQty[key].discountPercent || 0;
        // Best rate on offer for this quantity across every frequency.
        if (!discountByQty.has(qty) || pct > discountByQty.get(qty)) {
          discountByQty.set(qty, pct);
        }
      });
    });
    if (discountByQty.size < 2) return false;
    const percents = Array.from(discountByQty.values());
    return !percents.every((p) => p === percents[0]);
  }

  /**
   * Settle on cards vs stepper. An explicit setting always wins; 'auto' shows
   * the grid only where there are real quantity breaks to merchandise, so
   * single-tier products (the bundles) and flat-rate ones get the same plain
   * quantity control as one-time without anyone configuring it.
   */
  _resolveQuantityStyle() {
    if (this._quantityStylePreference !== 'auto') {
      this._quantityStyle = this._quantityStylePreference;
      return;
    }
    // Product cards have no stepper rendered to fall back to.
    this._quantityStyle = this._context === 'card' || this._hasQuantityBreaks() ? 'cards' : 'stepper';
  }

  /** Label for the stepper when it is the only quantity control on the page. */
  _stepperPrimaryLabel() {
    return (this.dataset.quantityLabel || '').trim() || 'Quantity';
  }

  /**
   * Perk pills inside the Subscribe card (stepper mode only).
   *
   * Priced against subscribing at the currently chosen quantity, whichever
   * purchase type is selected — the box states what subscribing would get you,
   * exactly as the price above it already does.
   */
  _updateSubscribePerks() {
    if (!this._subPerks) return;

    // In cards mode the pills belong to the quantity cards, not this box.
    if (this._quantityStyle !== 'stepper') {
      this._subPerks.hidden = true;
      return;
    }

    const qty = this._selectedQuantity || 1;
    const base = this._basePrice();
    if (base <= 0 || !this._canSubscribeAtQuantity(qty)) {
      this._subPerks.hidden = true;
      return;
    }

    const discount = this._subscribeDiscountForQuantity(qty);
    const lineTotal = this._subscriptionTotal(qty, discount);

    const showGift = this._qualifiesForFreeGift(lineTotal);
    // Absent when the gift block below is already naming the gift.
    if (this._subFreeGift) this._subFreeGift.hidden = !showGift;

    // Nothing to say: keep the row out of the flow rather than leaving a gap.
    this._subPerks.hidden = !(showGift && this._subFreeGift);
  }

  _syncSelectedUI() {
    this._qtyCards.forEach((card) => {
      const qty = parseInt(card.dataset.quantity, 10);
      card.classList.toggle('is-selected', qty === this._selectedQuantity);
      const radio = card.querySelector('[data-role="qty-radio"]');
      if (radio) radio.checked = qty === this._selectedQuantity;
    });
    // Sync the purchase-type cards too, so a server default or a URL deep-link
    // override (csw_subscribe) is reflected on load.
    this._ptCards.forEach((card) => {
      const isSelected = card.dataset.purchaseType === this._selectedPurchaseType;
      card.classList.toggle('is-selected', isSelected);
      const radio = card.querySelector('[data-role="pt-radio"]');
      if (radio) radio.checked = isSelected;
    });
  }

  _updateQtyCardDiscounts() {
    // Cards only appear in subscription mode (they are hidden in one-time mode).
    // We can safely resolve discounts against selling plans only.
    const base = this._basePrice();
    const currentFreq = this._frequencies.find((f) => f.key === this._selectedFrequencyKey);
    this._qtyCards.forEach((card) => {
      const qty = parseInt(card.dataset.quantity, 10);
      const discountEl = card.querySelector('[data-role="qty-discount"]');
      const perUnitEl = card.querySelector('[data-role="qty-per-unit"]');

      let discount = 0;
      const currentPlan = this._resolvePlan(currentFreq, qty);
      if (currentPlan) {
        discount = currentPlan.discountPercent || 0;
      } else {
        this._frequencies.forEach((f) => {
          const p = this._resolvePlan(f, qty);
          if (p && p.discountPercent > discount) discount = p.discountPercent;
        });
      }

      // Save X% pill, quoting the total saving against RRP so it matches the
      // struck-through price on the same row, not the plan discount alone.
      //
      // Shown on every row that has a saving. It used to stand down wherever a
      // merchant badge was set, because both sat on the card's top edge and
      // would have collided; the pill now rides inline with the per-unit price,
      // so "Most popular" and "Save 52%" can say their separate things.
      const savingPct = this._savingPercentVsRrp(qty, discount);
      if (discountEl) {
        if (savingPct > 0) {
          discountEl.textContent = `Save ${this._formatPercent(savingPct)}`;
          discountEl.hidden = false;
        } else {
          discountEl.textContent = '';
          discountEl.hidden = true;
        }
      }

      // Perk pills at the bottom of the card, both resolved against what this
      // quantity would actually cost as a subscription.
      const lineTotal = base > 0 ? this._subscriptionTotal(qty, discount) : 0;
      const freeGiftEl = card.querySelector('[data-role="qty-free-gift"]');
      if (freeGiftEl) {
        freeGiftEl.hidden = !(lineTotal > 0 && this._qualifiesForFreeGift(lineTotal));
      }

      // Per-unit price ("£19.99 each").
      if (perUnitEl) {
        if (base > 0) {
          const perUnitCents = this._subscriptionTotal(1, discount);
          perUnitEl.textContent = `${this._formatMoney(perUnitCents)} each`;
          perUnitEl.hidden = false;
        } else {
          perUnitEl.textContent = '';
          perUnitEl.hidden = true;
        }
      }

      // Line total with RRP struck through beneath it (rows layout only — the
      // element is display:none in the grid). Same pair the Subscribe card
      // shows, so the two agree at whatever quantity is selected.
      const priceEl = card.querySelector('[data-role="qty-price"]');
      if (priceEl) {
        if (lineTotal > 0) {
          const rrpTotal = this._rrpPrice() * qty;
          const nowEl = card.querySelector('[data-role="qty-price-now"]');
          const wasEl = card.querySelector('[data-role="qty-price-was"]');
          if (nowEl) nowEl.textContent = this._formatMoney(lineTotal);
          if (wasEl) {
            // Nothing to strike through when the plan saves nothing against RRP.
            const showWas = rrpTotal > lineTotal;
            wasEl.textContent = showWas ? this._formatMoney(rrpTotal) : '';
            wasEl.hidden = !showWas;
          }
          priceEl.hidden = false;
        } else {
          priceEl.hidden = true;
        }
      }
    });

    // Stepper mode carries the same information in the Subscribe card instead.
    this._updateSubscribePerks();
  }

  /**
   * Does a subscription line worth `lineTotal` cents earn the free gift?
   * On the 'subscription' trigger every subscribing quantity earns it, so the
   * line total is irrelevant — callers must only ask while subscribing.
   */
  _qualifiesForFreeGift(lineTotal) {
    if (!this._freeGiftActive) return false;
    if (this._giftTrigger === 'subscription') return true;
    return this._freeGiftThreshold > 0 && lineTotal >= this._freeGiftThreshold;
  }

  _updatePurchaseTypeSummaries() {
    this._applyCopyTokens();

    if (this._ptSubscribeSavings) {
      // A benefit list already covers this ground — and usually repeats the
      // reassurance verbatim — so the line is left out when one is present.
      const hasBenefits = this.dataset.hasSubscribeBenefits === 'true';
      // Prefer the merchant-configured reassurance text (e.g. "Delay or cancel any time").
      // Fall back to "Up to X% off" if the setting is blank.
      const reassurance = hasBenefits ? '' : (this.dataset.subscribeReassurance || '').trim();
      if (hasBenefits) {
        this._ptSubscribeSavings.textContent = '';
      } else if (reassurance) {
        this._ptSubscribeSavings.textContent = reassurance;
      } else {
        const bestSaving = this._savingPercentVsRrp(1, this._maxDiscountPercent());
        this._ptSubscribeSavings.textContent = bestSaving > 0 ? `Up to ${this._formatPercent(bestSaving)} off` : '';
      }
    }
  }

  /**
   * Fill {max_saving_percentage} in the benefit / drawback copy.
   *
   * Merchant copy is a plain textarea, so the token is the only way to quote a
   * live figure in it. The value is the best saving against RRP on offer — the
   * same number the top quantity row shows, since saving-versus-RRP doesn't
   * change with quantity, only the plan discount behind it does.
   *
   * The raw line is cached on first pass: variant changes re-run this, and the
   * token is gone from the DOM once substituted.
   */
  _applyCopyTokens() {
    const items = this.querySelectorAll('.custom-subscription-widget__pt-list-item');
    if (!items.length) return;

    const best = this._savingPercentVsRrp(1, this._maxDiscountPercent());
    const value = best > 0 ? this._formatPercent(best) : '';

    items.forEach((item) => {
      // Text sits alongside the icon, so substitute in the text node rather than
      // rewriting textContent and losing the svg.
      const node = Array.from(item.childNodes).find(
        (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
      );
      if (!node) return;
      if (item.dataset.copyRaw === undefined) item.dataset.copyRaw = node.textContent;
      node.textContent = item.dataset.copyRaw.replace(/\{max_saving_percentage\}/g, value);
    });
  }

  /**
   * Discount percent that applies to a subscription of `qty` units: the selected
   * frequency's plan when one covers that quantity, else the best on offer for it.
   * Returns 0 when no plan covers the quantity (e.g. above the subscribe max).
   */
  _subscribeDiscountForQuantity(qty) {
    const freq = this._frequencies.find((f) => f.key === this._selectedFrequencyKey);
    const selected = this._resolvePlan(freq, qty);
    if (selected) return selected.discountPercent || 0;
    let best = 0;
    this._frequencies.forEach((f) => {
      const plan = this._resolvePlan(f, qty);
      if (plan && plan.discountPercent > best) best = plan.discountPercent;
    });
    return best;
  }

  _currentPlan() {
    const freq = this._frequencies.find((f) => f.key === this._selectedFrequencyKey);
    return this._resolvePlan(freq, this._selectedQuantity);
  }

  /** Largest discount percent offered across all frequencies/quantities. */
  _maxDiscountPercent() {
    let best = 0;
    this._frequencies.forEach((f) => {
      this._plansIn(f).forEach((p) => {
        if (p.discountPercent > best) best = p.discountPercent;
      });
    });
    return best;
  }

  /**
   * True when every plan on offer discounts by the same amount — i.e. the saving
   * does not vary by quantity or frequency. Used to strip the per-option "(X%
   * off)" suffix from the frequency dropdown, where repeating one figure on
   * every line is noise rather than information.
   */
  _hasFlatDiscount() {
    const percents = [];
    this._frequencies.forEach((f) => {
      this._plansIn(f).forEach((p) => percents.push(p.discountPercent || 0));
    });
    if (percents.length < 2) return true;
    return percents.every((p) => p === percents[0]);
  }

  _updateFormInputs() {
    if (this._quantityInput) this._quantityInput.value = String(this._selectedQuantity);
    if (this._sellingPlanInput) {
      if (this._selectedPurchaseType === 'subscription') {
        const plan = this._currentPlan();
        this._sellingPlanInput.value = plan ? String(plan.id) : '';
      } else {
        this._sellingPlanInput.value = '';
      }
    }
    const legacyQty = document.querySelector(`#Quantity-${this._sectionId}`);
    if (legacyQty && legacyQty !== this._quantityInput) {
      legacyQty.value = String(this._selectedQuantity);
    }
    // Publish the quantity on the form itself, for product-form.js to apply to
    // the FormData just before it posts. Our own hidden input can't be trusted
    // to carry it on its own: it is form-associated from outside the <form>, so
    // anything a bundle app prepends inside the form serialises after it and
    // wins. A data attribute has no such collision — see the note at the
    // csw_quantity handling in product-form.js.
    const form = document.getElementById(this._productFormId);
    if (form) form.dataset.cswQuantity = String(this._selectedQuantity);
  }

  _basePrice() {
    const entry = this._variantPrices[this._currentVariantId];
    return entry ? parseInt(entry.price, 10) || 0 : 0;
  }

  /**
   * The WB Campaign price block on this section, when one is rendered (PDP
   * only). Re-queried each time because product-info.js swaps the price
   * wrapper on variant change.
   */
  _campaignEl() {
    if (this._context === 'card') return null;
    const container = document.getElementById(`price-${this._sectionId}`);
    return container ? container.querySelector('wb-campaign-price') : null;
  }

  /**
   * Per-unit amount the campaign takes off the standard price for a purchase
   * type. Derived from the hand-entered single-unit price, so quantity 1 shows
   * exactly that figure and higher quantities carry the same saving per unit —
   * which is how an "amount off products" automatic discount applies at
   * checkout. 0 without a campaign block, or with no price set for that type.
   */
  _campaignUnitOff(isSub) {
    const el = this._campaignEl();
    if (!el) return 0;
    const campaignPrice = parseInt(isSub ? el.dataset.subscriptionPrice : el.dataset.oneTimePrice, 10) || 0;
    if (campaignPrice <= 0) return 0;
    const standard = isSub ? this._standardUnitPrice(this._singleUnitDiscount()) : this._basePrice();
    return Math.max(0, standard - campaignPrice);
  }

  /** Discount percent a one-unit subscription gets at the selected frequency. */
  _singleUnitDiscount() {
    const freq = this._frequencies.find((f) => f.key === this._selectedFrequencyKey);
    let plan = freq ? this._resolvePlan(freq, 1) : null;
    if (!plan) plan = this._frequencies.map((f) => this._resolvePlan(f, 1)).find(Boolean);
    return plan ? plan.discountPercent || 0 : 0;
  }

  /** Unit price at a plan discount before any campaign saving. */
  _standardUnitPrice(discountPct) {
    return Math.round(this._basePrice() * (1 - (discountPct || 0) / 100));
  }

  /** Line total for a one-time purchase of `qty` units. */
  _oneTimeTotal(qty) {
    const units = qty || 1;
    return Math.max(0, this._basePrice() - this._campaignUnitOff(false)) * units;
  }

  /**
   * Line total for a subscription of `qty` units at `discountPct`.
   *
   * Shopify applies a selling plan's percentage adjustment to the unit price and
   * rounds there, then multiplies by quantity. Rounding the line total instead
   * drifts a penny from checkout on fractional percentages (3 x 37.5% off £15.99
   * is £29.97, not £29.98), so every price the widget shows goes through here.
   * A campaign saving comes off each unit after that, the way an automatic
   * discount does.
   */
  _subscriptionTotal(qty, discountPct) {
    const units = qty || 1;
    return Math.max(0, this._standardUnitPrice(discountPct) - this._campaignUnitOff(true)) * units;
  }

  /**
   * What the customer actually saves against RRP, as a percent: the sale
   * discount already baked into the selling price plus the subscription
   * discount on top. Every percentage shown to the customer uses this, so the
   * figures agree with the struck-through prices beside them.
   */
  _savingPercentVsRrp(qty, discountPct) {
    const units = qty || 1;
    const rrpTotal = this._rrpPrice() * units;
    if (rrpTotal <= 0) return 0;
    const payTotal = this._subscriptionTotal(units, discountPct);
    return ((rrpTotal - payTotal) / rrpTotal) * 100;
  }

  /**
   * RRP — Shopify's compare-at price, which is what every struck-through price
   * must show. Falls back to the selling price when no compare-at is set, so a
   * product that isn't on sale still compares against something sensible.
   */
  _rrpPrice() {
    const entry = this._variantPrices[this._currentVariantId];
    const compareAt = entry ? parseInt(entry.compare_at_price, 10) || 0 : 0;
    const base = this._basePrice();
    return compareAt > base ? compareAt : base;
  }

  _formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    const currency = this.dataset.currency || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (e) {
      return `${(cents / 100).toFixed(2)}`;
    }
  }

  /**
   * Percentages are shown as whole numbers, everywhere.
   *
   * Every figure that reaches here is division on rounded pence — a plan's
   * adjustment against an RRP — so a decimal is arithmetic noise rather than
   * precision, and "Save 37.5%" reads as a number someone calculated rather
   * than an offer. Rounding in one place also keeps the badge on a quantity
   * row, the frequency option and the button label from disagreeing by a
   * tenth.
   */
  _formatPercent(value) {
    return `${Math.round(value)}%`;
  }

  /**
   * Update dynamic CTA labels + prices.
   * - Card context: two buttons (Add to cart + Subscribe & save X%). Subscribe is
   *   hidden when no discount is available.
   * - PDP context: single Dawn submit button whose label reflects the current
   *   purchase type ("Add to cart - £X" for one-time, "Subscribe & save X% - £Y"
   *   for subscription). Pre-order products keep the "Pre-Order" prefix.
   */
  _updateCtaLabels() {
    if (this._context === 'card') {
      this._updateCardCtaLabels();
    } else {
      this._updatePdpCtaLabel();
      this._updatePdpPrice();
      this._updatePdpCtaNote();
      this._updateAtcFooterDelivery();
    }
  }

  /**
   * Flip the ATC footer's delivery heading to "FREE Express shipping" while the
   * subscription option is selected.
   *
   * The footer is server-rendered from the *cart* total, which on a PDP is
   * usually empty — so without this, a shopper about to subscribe never sees that
   * their delivery is free. Restores the server heading for one-time.
   */
  _updateAtcFooterDelivery() {
    if (this.dataset.subscriptionFreeDelivery !== 'true') return;
    // Re-query in case product-info.js has replaced the section after a variant change.
    if (!this._atcFooterDelivery || !this._atcFooterDelivery.isConnected) {
      this._atcFooterDelivery = document.getElementById(`AtcFooterDelivery-${this._sectionId}`);
      // Cache the server-rendered heading: it already says "FREE ..." when the
      // cart clears the spend threshold, so that is what one-time restores to.
      this._atcFooterInitialText = this._atcFooterDelivery
        ? this._atcFooterDelivery.textContent.trim()
        : '';
    }
    if (!this._atcFooterDelivery) return;

    const el = this._atcFooterDelivery;
    const text =
      this._selectedPurchaseType === 'subscription'
        ? el.dataset.freeHeading || this._atcFooterInitialText
        : this._atcFooterInitialText;
    if (text) el.textContent = text;
  }

  _updatePdpCtaNote() {
    // Re-query in case product-info.js has replaced the form after a variant change.
    if (!this._pdpCtaNote || !this._pdpCtaNote.isConnected) {
      this._pdpCtaNote = document.getElementById(`ProductCtaNote-${this._sectionId}`);
    }
    if (!this._pdpCtaNote) return;

    const text = (this.dataset.subscribeReassurance || '').trim();
    const show =
      text !== '' &&
      this._selectedPurchaseType === 'subscription' &&
      !(this._pdpSubmitButton && this._pdpSubmitButton.disabled);

    this._pdpCtaNote.textContent = text;
    this._pdpCtaNote.hidden = !show;
  }

  _updateCardCtaLabels() {
    const qty = this._selectedQuantity || 1;
    const base = this._basePrice();
    const hasBase = base > 0;

    if (this._ctaOneTimePrice) {
      this._ctaOneTimePrice.textContent = hasBase ? this._formatMoney(base * qty) : '';
    }

    if (this._ctaSubscribe) {
      // The card Subscribe CTA deep-links to the PDP, so we advertise the best
      // available saving ("up to X%") rather than a quantity/frequency-specific
      // price, which would be misleading before the customer picks a frequency.
      const maxDiscount = this._maxDiscountPercent();
      const maxSaving = this._savingPercentVsRrp(1, maxDiscount);
      if (maxDiscount > 0) {
        this._ctaSubscribe.hidden = false;
        if (this._ctaSubscribeLabel) {
          this._ctaSubscribeLabel.textContent = `Subscribe & save up to ${this._formatPercent(maxSaving)}`;
        }
        // Clear any specific price (hidden via `.cta-price:empty`).
        if (this._ctaSubscribePrice) this._ctaSubscribePrice.textContent = '';
      } else {
        this._ctaSubscribe.hidden = true;
      }
    }
  }

  _updatePdpCtaLabel() {
    // Re-query in case product-info.js has replaced the button after a variant change.
    if (!this._pdpSubmitButton || !this._pdpSubmitButton.isConnected) {
      this._pdpSubmitButton = document.getElementById(`ProductSubmitButton-${this._sectionId}`);
      this._pdpCtaTextEl = this._pdpSubmitButton
        ? this._pdpSubmitButton.querySelector('[data-role="pdp-cta-text"]')
        : null;
    }
    if (!this._pdpSubmitButton || !this._pdpCtaTextEl) return;
    if (this._pdpSubmitButton.disabled) return; // Sold-out / unavailable — leave label alone.

    const qty = this._selectedQuantity || 1;
    const base = this._basePrice();
    if (base <= 0) return;

    const isPreOrder = this._pdpSubmitButton.dataset.preOrder === 'true';
    const isSub = this._selectedPurchaseType === 'subscription';

    // With Kaching owning one-time quantity, its selected bar — not our state —
    // is the price. Quote the figure the bar itself is showing rather than
    // recomputing it: bundle pricing is Kaching's, so anything we worked out
    // could disagree with what the customer is looking at. Falls back to the
    // bare label when the bar can't be read, which is the honest failure.
    if (this._hasKaching && !isSub) {
      const lead = isPreOrder ? 'Pre-Order' : 'Add to cart';
      const kachingPrice = this._kachingSelectedPrice().now;
      this._pdpCtaTextEl.textContent = kachingPrice ? `${lead} - ${kachingPrice}` : lead;
      return;
    }

    const plan = isSub ? this._currentPlan() : null;
    const discountPct = plan && plan.discountPercent ? plan.discountPercent : 0;

    let total;
    let leadLabel;
    if (isSub && discountPct > 0) {
      total = this._subscriptionTotal(qty, discountPct);
      const savingPct = this._savingPercentVsRrp(qty, discountPct);
      leadLabel = isPreOrder
        ? `Pre-Order & subscribe (${this._formatPercent(savingPct)} off)`
        : `Subscribe & save ${this._formatPercent(savingPct)}`;
    } else if (isSub) {
      total = this._subscriptionTotal(qty, 0);
      leadLabel = isPreOrder ? 'Pre-Order & subscribe' : 'Subscribe';
    } else {
      total = this._oneTimeTotal(qty);
      leadLabel = isPreOrder ? 'Pre-Order' : 'Add to cart';
    }

    this._pdpCtaTextEl.textContent = `${leadLabel} - ${this._formatMoney(total)}`;
  }

  /**
   * Update the PDP headline price to reflect the selected purchase option.
   *
   * - Subscription with a discount (qty within the card range): show the
   *   discounted line total (base x qty x (1 - discount)) as the headline with
   *   the original line total as a strikethrough compare-at. This matches the
   *   Add to cart button total.
   * - One-time, no discount, over-max qty, or missing price data: restore the
   *   original server-rendered price untouched.
   *
   * The pristine markup is captured the first time each `.price` node is seen.
   * product-info.js replaces `#price-{sectionId}`'s contents on variant change
   * (before publishing variantChange, which we subscribe to), so a node we have
   * not met is fresh from the server. "Doesn't carry our marker class" is not
   * the same thing: Kaching Bundles writes its selected bar into the theme's
   * price elements too, and a copy cached after that restores the bar's RRP
   * under Subscribe & save.
   */
  _updatePdpPrice() {
    const container = document.getElementById(`price-${this._sectionId}`);
    if (!container) return;
    const campaign = container.querySelector('wb-campaign-price');
    if (campaign) {
      this._syncCampaignPrice(campaign);
      return;
    }
    const priceEl = container.querySelector('.price');
    if (!priceEl) return;

    if (priceEl !== this._pdpPriceEl) {
      // A node we haven't seen is server-rendered — cache it so we can restore
      // later, and grab the localized regular/sale a11y labels.
      this._pdpPriceEl = priceEl;
      this._pdpPriceOriginalHTML = priceEl.innerHTML;
      this._pdpPriceOriginalClass = priceEl.className;
      const labels = priceEl.querySelectorAll('.price__sale .visually-hidden');
      this._pdpRegularLabel = labels[0] ? labels[0].textContent : '';
      this._pdpSaleLabel = labels[1] ? labels[1].textContent : '';
    }

    const base = this._basePrice();
    const isSub = this._selectedPurchaseType === 'subscription';
    const overMax = !this._canSubscribeAtQuantity(this._selectedQuantity);
    const plan = isSub && !overMax ? this._currentPlan() : null;
    const discountPct = plan && plan.discountPercent ? plan.discountPercent : 0;

    // One-time under Kaching: the selected bar is the price, so quote the bar
    // rather than base x quantity — the bars are the quantity control here, and
    // the bundle discount is the app's to work out. Falls through to the restore
    // below while no bar can be read, which is the same as no Kaching at all.
    if (this._hasKaching && !isSub) {
      const bar = this._kachingSelectedPrice();
      if (bar.now) {
        // Badge is worked out from the pair on the bar, not from compare_at, so
        // it always describes the two numbers directly above it — same rule the
        // subscription path follows. A bar with nothing struck through saves
        // nothing, and 0 restores whatever badge the theme rendered.
        this._updatePdpSaleBadge(this._kachingSavingPercent(bar));
        this._writePdpPricePair(priceEl, this._escapeHtml(bar.now), this._escapeHtml(bar.was));
        return;
      }
    }

    // Restore the original price for one-time / no-discount / missing data.
    if (!isSub || overMax || discountPct <= 0 || base <= 0) {
      // Compared rather than flagged, so figures another script left behind
      // (Kaching's bar, after switching away from one-time) are put right too.
      if (this._pdpPriceOriginalHTML != null && priceEl.innerHTML !== this._pdpPriceOriginalHTML) {
        priceEl.innerHTML = this._pdpPriceOriginalHTML;
        priceEl.className = this._pdpPriceOriginalClass;
      }
      this._updatePdpSaleBadge(0);
      return;
    }

    const qty = this._selectedQuantity || 1;
    // Struck price is always the RRP; the subscription discount comes off the
    // selling price, so on a discounted product the two differ.
    const originalTotal = this._rrpPrice() * qty;
    const discountedTotal = this._subscriptionTotal(qty, discountPct);
    // Badge reflects the pair actually shown, i.e. the full saving against RRP.
    const savingPct = originalTotal > 0 ? ((originalTotal - discountedTotal) / originalTotal) * 100 : 0;
    this._updatePdpSaleBadge(savingPct);
    this._writePdpPricePair(
      priceEl,
      this._formatMoney(discountedTotal),
      this._formatMoney(originalTotal)
    );
  }

  /**
   * Hand the headline to the WB Campaign price block, which renders the figures
   * itself (wb-campaign-price.js). The widget only says what they are, as
   * attributes, so the order the two scripts load in doesn't matter.
   */
  _syncCampaignPrice(el) {
    const qty = this._selectedQuantity || 1;
    const isSub = this._selectedPurchaseType === 'subscription' && this._canSubscribeAtQuantity(qty);
    const plan = isSub ? this._currentPlan() : null;
    const discountPct = plan && plan.discountPercent ? plan.discountPercent : 0;
    const now = isSub ? this._subscriptionTotal(qty, discountPct) : this._oneTimeTotal(qty);
    el.dataset.purchaseType = isSub ? 'subscription' : 'one_time';
    el.dataset.quantity = String(qty);
    el.dataset.active = this._campaignUnitOff(isSub) > 0 && now > 0 ? 'true' : 'false';
    el.dataset.now = String(now);
    el.dataset.was = String(this._rrpPrice() * qty);
    const standard = this._standardUnitPrice(discountPct) * qty;
    el.dataset.standard = String(standard);
    el.dataset.recurring = String(standard);
  }

  /**
   * Write a now/was pair into Dawn's price block, in the markup the theme's own
   * sale price uses so it inherits the theme's styling. `was` is optional — with
   * nothing to strike through, only the current price is written.
   *
   * Both arguments are HTML: callers pass either _formatMoney output or a value
   * put through _escapeHtml.
   */
  _writePdpPricePair(priceEl, nowHtml, wasHtml) {
    const saleEl = priceEl.querySelector('.price__sale');
    if (!saleEl) return;
    priceEl.classList.add('price--on-sale', 'csw-price-modified');
    saleEl.innerHTML =
      `<span class="visually-hidden visually-hidden--inline">${this._pdpRegularLabel}</span>` +
      (wasHtml ? `<span><s class="price-item price-item--regular">${wasHtml}</s></span>` : '') +
      `<span class="visually-hidden visually-hidden--inline">${this._pdpSaleLabel}</span>` +
      `<span class="price-item price-item--sale price-item--last">${nowHtml}</span>`;
  }

  /**
   * Saving on a Kaching bar, from the two prices it renders. 0 when either can't
   * be read or there's nothing struck through, which reads as "no saving" and
   * leaves the theme's own badge in place.
   *
   * Rounded to whole percent, unlike the subscription figures: those come from
   * configured plan discounts where a .5 is a real setting, while this one is
   * division on two rounded money amounts, so its decimal is arithmetic noise —
   * 58.3% off a 3-pack reads as false precision.
   */
  _kachingSavingPercent(bar) {
    const now = this._parseMoney(bar.now);
    const was = this._parseMoney(bar.was);
    if (!(was > 0) || !(now >= 0) || now >= was) return 0;
    return Math.round(((was - now) / was) * 100);
  }

  /**
   * Money string back to cents, for figures that arrive as rendered text rather
   * than numbers. Locale-agnostic: the last '.' or ',' is the decimal separator
   * only when one or two digits follow it, so "£1,234.56" and "1.234,56 €" both
   * come back as 123456 while "1,234" stays 123400. NaN if there's no number in
   * there at all — callers treat that as "don't know" rather than zero.
   */
  _parseMoney(text) {
    const cleaned = String(text || '').replace(/[^\d.,]/g, '');
    if (!cleaned) return NaN;
    const separator = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    const decimals = separator > -1 ? cleaned.length - separator - 1 : 0;
    const hasFraction = decimals > 0 && decimals <= 2;
    const whole = parseInt(
      (hasFraction ? cleaned.slice(0, separator) : cleaned).replace(/\D/g, ''),
      10
    );
    if (isNaN(whole)) return NaN;
    const fraction = hasFraction ? parseInt(cleaned.slice(separator + 1).padEnd(2, '0'), 10) : 0;
    return whole * 100 + fraction;
  }

  /** For text lifted out of another app's DOM before it goes back in as HTML. */
  _escapeHtml(value) {
    return String(value || '').replace(
      /[&<>"]/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
    );
  }

  /**
   * Keep the "Save X%" badge in the PDP price block in step with the selection.
   *
   * The badge is server-rendered from compare_at_price vs price, so it otherwise
   * stays frozen on whatever discount the page loaded with while the customer
   * changes purchase type, quantity or frequency.
   *
   * - discountPct > 0 (subscription): show that discount, reusing the theme's
   *   badge when one is present, otherwise adding one.
   * - discountPct = 0 (one-time / over-max / no discount): restore the badges the
   *   theme rendered, so a genuine compare-at sale badge comes back untouched.
   */
  _updatePdpSaleBadge(discountPct) {
    const container = document.getElementById(`price-${this._sectionId}`);
    if (!container) return;
    // The campaign block carries its own badge; nothing here to keep in step.
    if (container.querySelector('wb-campaign-price')) return;
    const badgeWrap = container.querySelector('.card__badge');
    if (!badgeWrap) return;

    // product-info.js replaces the whole price block on variant change, so a
    // wrapper without our marker is pristine and safe to cache.
    const wasModified = badgeWrap.classList.contains('csw-badge-modified');
    if (!wasModified) this._pdpBadgeOriginalHTML = badgeWrap.innerHTML;

    if (discountPct <= 0) {
      if (wasModified && this._pdpBadgeOriginalHTML != null) {
        badgeWrap.innerHTML = this._pdpBadgeOriginalHTML;
        badgeWrap.classList.remove('csw-badge-modified');
      }
      return;
    }

    let saveBadge = badgeWrap.querySelector('[data-role="csw-save-badge"]');
    if (!saveBadge) {
      // Adopt the theme's own savings badge when it rendered one.
      saveBadge = Array.from(badgeWrap.querySelectorAll('.badge')).find((el) =>
        /save\s*\d+(\.\d+)?\s*%/i.test(el.textContent)
      );
      if (saveBadge) saveBadge.setAttribute('data-role', 'csw-save-badge');
    }
    if (!saveBadge) {
      saveBadge = document.createElement('span');
      saveBadge.className = `badge badge--bottom-left ${this.dataset.saleBadgeClass || ''}`.trim();
      saveBadge.setAttribute('data-role', 'csw-save-badge');
      badgeWrap.prepend(saveBadge);
    }
    saveBadge.textContent = `Save ${this._formatPercent(discountPct)}`;
    badgeWrap.classList.add('csw-badge-modified');
  }

  /**
   * Savings summary under the frequency, in the shape Kaching Bundles gives the
   * same statement under its bars.
   *
   * Counts the discount and nothing else. The free gift is deliberately left
   * out: Kaching renders its own summary on the same page with the same
   * wording and its includeGiftValue is off, so folding the gift in here would
   * put two different figures for one order side by side. It also keeps the
   * amount reconcilable — RRP minus what you pay, both of which are on the
   * quantity row directly above.
   *
   * Hidden whenever there's nothing to state — one-time, no discount, or a
   * quantity past what a subscription covers.
   */
  _updateSavings() {
    if (!this._savingsEl) return;
    const copyEl = this._savingsEl.querySelector('[data-role="savings-copy"]');
    const template = (this.dataset.savingsSummary || '').trim();
    const hide = () => {
      if (copyEl) copyEl.textContent = '';
      this._savingsEl.hidden = true;
    };
    if (
      !copyEl ||
      !template ||
      this._selectedPurchaseType !== 'subscription' ||
      !this._canSubscribeAtQuantity(this._selectedQuantity)
    ) {
      hide();
      return;
    }
    const plan = this._currentPlan();
    const qty = this._selectedQuantity;
    const base = this._basePrice();
    // A plan with no discount saves nothing over buying once, whatever the RRP
    // says, so it gets no savings line — unless a campaign price is what makes
    // subscribing cheaper.
    if (!plan || !base || (!plan.discountPercent && !this._campaignUnitOff(true))) {
      hide();
      return;
    }
    // Measured against RRP, matching the struck-through price shown alongside.
    const rrpTotal = this._rrpPrice() * qty;
    const payTotal = this._subscriptionTotal(qty, plan.discountPercent || 0);
    const savedCents = rrpTotal - payTotal;
    if (savedCents <= 0) {
      hide();
      return;
    }
    const savedPct = (savedCents / rrpTotal) * 100;

    copyEl.textContent = '';
    // Split on the token rather than writing HTML: the copy is a merchant
    // setting, and the amount is the only part that gets its own styling.
    template.split(/(\{saved_total\})/).forEach((part) => {
      if (part === '{saved_total}') {
        const value = document.createElement('span');
        value.className = 'custom-subscription-widget__savings-value';
        value.textContent = this._formatMoney(savedCents);
        copyEl.appendChild(value);
        return;
      }
      if (!part) return;
      copyEl.appendChild(
        document.createTextNode(
          part.replace(/\{saved_percentage\}/g, this._formatPercent(savedPct))
        )
      );
    });
    this._savingsEl.hidden = false;
  }
}

if (!customElements.get('custom-subscription-widget')) {
  customElements.define('custom-subscription-widget', CustomSubscriptionWidget);
}
